import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Cog,
  Wrench,
  Package,
  Cpu,
  Hammer,
  GraduationCap,
  type LucideIcon,
} from 'lucide-react'
import { EventCard } from '@/components/EventCard'
import { MachineCard } from '@/components/MachineCard'
import { NewsCard } from '@/components/NewsCard'
import { Section } from '@/components/Section'
import { LocalizedLink } from '@/components/LocalizedLink'
import { JsonLd } from '@/components/SEO/JsonLd'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'
import { usePageSEO } from '@/hooks/usePageSEO'
import type { SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { cn } from '@/lib/utils'

interface FeaturedMachine {
  slug: string
  image: string
  nameKey: string
  descKey: string
  categoryKey: string
}

interface FeaturedService {
  key: 'design' | 'assistance' | 'spareParts' | 'retrofit' | 'mechanical' | 'training'
  icon: LucideIcon
}

interface FeaturedNews {
  slug: string
  image: string
  date: string
  titleKey: string
  excerptKey: string
}

interface FeaturedEvent {
  slug: string
  image: string
  date: string
  titleKey: string
  locationKey: string
}

const FEATURED_MACHINES: FeaturedMachine[] = [
  {
    slug: 'fb-75',
    image: '/images/machines/fenix-fb-75.jpg',
    nameKey: 'Fenix FB 75',
    descKey: 'home.featuredMachines.fb75.description',
    categoryKey: 'machines.categories.tangential',
  },
  {
    slug: 'fa-60',
    image: '/images/machines/fenix-fa-60.jpg',
    nameKey: 'Fenix FA 60',
    descKey: 'home.featuredMachines.fa60.description',
    categoryKey: 'machines.categories.tangential',
  },
  {
    slug: 'fc-100',
    image: '/images/machines/fenix-fc-100.jpg',
    nameKey: 'Fenix FC 100',
    descKey: 'home.featuredMachines.fc100.description',
    categoryKey: 'machines.categories.tangential',
  },
  {
    slug: 'fd-150',
    image: '/images/machines/fenix-fd-150.jpg',
    nameKey: 'Fenix FD 150',
    descKey: 'home.featuredMachines.fd150.description',
    categoryKey: 'machines.categories.tangential',
  },
  {
    slug: 'fr-60',
    image: '/images/machines/fenix-fr-60.jpg',
    nameKey: 'Fenix FR 60',
    descKey: 'home.featuredMachines.fr60.description',
    categoryKey: 'machines.categories.tangential',
  },
  {
    slug: 'ff-u-200',
    image: '/images/machines/fenix-ff-u-200.jpg',
    nameKey: 'Fenix FF-U 200',
    descKey: 'home.featuredMachines.ffU200.description',
    categoryKey: 'machines.categories.doubleColumn',
  },
]

const FEATURED_SERVICES: FeaturedService[] = [
  { key: 'design', icon: Cog },
  { key: 'assistance', icon: Wrench },
  { key: 'spareParts', icon: Package },
  { key: 'retrofit', icon: Cpu },
  { key: 'mechanical', icon: Hammer },
  { key: 'training', icon: GraduationCap },
]

const FEATURED_NEWS: FeaturedNews[] = [
  {
    slug: 'rettifica-meccanica-principi',
    image: '/images/news/rettifica-meccanica.jpg',
    date: '2024-09-12',
    titleKey: 'home.featuredNews.rettificaMeccanica.title',
    excerptKey: 'home.featuredNews.rettificaMeccanica.excerpt',
  },
  {
    slug: 'fenix-siemens-sinergia',
    image: '/images/news/fenix-siemens.jpg',
    date: '2024-06-21',
    titleKey: 'home.featuredNews.siemens.title',
    excerptKey: 'home.featuredNews.siemens.excerpt',
  },
]

const FEATURED_EVENTS: FeaturedEvent[] = [
  {
    slug: 'mecspe-2024',
    image: '/images/events/mecspe-2023.jpg',
    date: '2024-03-06',
    titleKey: 'home.featuredEvents.mecspe2024.title',
    locationKey: 'home.featuredEvents.mecspe2024.location',
  },
  {
    slug: 'global-industrie-2024',
    image: '/images/events/global-industrie.jpg',
    date: '2024-03-25',
    titleKey: 'home.featuredEvents.globalIndustrie.title',
    locationKey: 'home.featuredEvents.globalIndustrie.location',
  },
]

export default function HomePage() {
  const { t, i18n } = useTranslation()
  const navigate = useLocalizedNavigate()
  const currentLang = (i18n.language || 'it') as SupportedLanguage

  usePageSEO({
    title: t('meta.home.title'),
    description: t('meta.home.description'),
    ogTitle: t('meta.home.ogTitle'),
    ogDescription: t('meta.home.ogDescription'),
    ogImage: '/images/hero/industrial.jpg',
  })

  const servicesSegment = localizedRoutes.services[currentLang]
  const newsSegment = localizedRoutes.news[currentLang]
  const eventsSegment = localizedRoutes.events[currentLang]

  // Organization schema — JSON-LD payload for the Home page. Absolute URLs
  // are critical for schema.org consumption; fall back to a safe default
  // origin when the window object is not yet available (SSR-safe).
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : t('schema.fallbackDomain')

  const organizationSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: t('common.brandName'),
      legalName: t('contacts.companyName'),
      url: origin,
      logo: `${origin}/images/logo.png`,
      description: t('meta.home.description'),
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: t('schema.contactType'),
          telephone: t('contact.phone'),
          email: t('contact.email'),
          availableLanguage: t('schema.availableLanguage'),
        },
      ],
      address: {
        '@type': 'PostalAddress',
        streetAddress: t('contact.address'),
        addressLocality: t('meta.addressLocality'),
        addressRegion: t('meta.addressRegion'),
        postalCode: t('meta.addressPostalCode'),
        addressCountry: t('meta.addressCountry'),
      },
      vatID: t('contact.vat'),
    }),
    [origin, t]
  )

  return (
    <main id="main-content" className="bg-ink-100">
      <JsonLd id="organization" data={organizationSchema} />
      {/* ============================================================
          1 · HERO — industrial block with coordinate frame,
          hero image, dark blue overlay, and two CTAs
          ============================================================ */}
      <section
        aria-labelledby="home-hero-title"
        className="relative isolate overflow-hidden bg-ink-900 text-white"
      >
        {/* Background image */}
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-20 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/hero/industrial.jpg)' }}
        />
        {/* Primary gradient overlay */}
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-br from-ink-900/92 via-ink-900/78 to-brand-900/80"
        />
        {/* Brand-blue wash (industrial tint) */}
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 mix-blend-multiply bg-brand-800/30"
        />
        {/* Faint diagonal engineering stripes bleeding in from the right */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-[55%] opacity-[0.12] fx-diagonal-accent"
        />
        {/* Radial brand glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-40 top-1/3 h-[28rem] w-[28rem] -translate-y-1/2 rounded-full bg-brand-500/25 blur-3xl"
        />

        {/* Top coordinate rail */}
        <div
          aria-hidden="true"
          className="absolute top-24 md:top-28 left-0 right-0 flex items-center gap-4 px-4 sm:px-6 lg:px-8 text-[10px] font-semibold uppercase tracking-[0.35em] text-brand-300/70"
        >
          <span className="block h-px w-6 bg-brand-400/60" />
          <span>{t('home.hero.coord')}</span>
          <span className="block h-px flex-1 bg-white/10" />
          <span className="hidden sm:block">{t('meta.coordinates')}</span>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-36 pb-24 md:pt-40 md:pb-32 lg:pt-48 lg:pb-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-8">
              {/* Eyebrow badge */}
              <div className="mb-6 inline-flex items-center gap-3 rounded-sm border border-brand-400/30 bg-brand-500/10 px-3.5 py-1.5 backdrop-blur-sm">
                <span
                  aria-hidden="true"
                  className="block h-1.5 w-1.5 rounded-full bg-brand-400"
                />
                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-200">
                  {t('home.hero.badge')}
                </span>
              </div>

              {/* Title */}
              <h1
                id="home-hero-title"
                className="fx-text-balance font-bold leading-[1.02] text-white text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem]"
              >
                {t('home.hero.title')}
              </h1>

              {/* Subtitle */}
              <p className="mt-6 max-w-2xl fx-text-pretty text-base md:text-lg leading-relaxed text-white/80">
                {t('home.hero.subtitle')}
              </p>

              {/* CTAs */}
              <div className="mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-4">
                <LocalizedLink
                  to="machines"
                  className="group relative inline-flex items-center justify-center gap-3 rounded-sm bg-brand-500 px-7 py-4 text-sm md:text-base font-bold uppercase tracking-[0.08em] text-white shadow-[0_10px_30px_-12px_rgba(74,155,220,0.6)] transition-[background-color,transform,box-shadow] duration-200 hover:bg-brand-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
                >
                  <span
                    aria-hidden="true"
                    className="absolute -left-[3px] top-1/2 block h-6 w-[3px] -translate-y-1/2 bg-white/95 transition-all duration-200 group-hover:h-8"
                  />
                  {t('home.hero.cta')}
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                    strokeWidth={2.75}
                  />
                </LocalizedLink>

                <LocalizedLink
                  to="contacts"
                  className="group inline-flex items-center justify-center gap-3 rounded-sm border border-white/30 bg-white/5 px-7 py-4 text-sm md:text-base font-bold uppercase tracking-[0.08em] text-white backdrop-blur-sm transition-[background-color,border-color,transform] duration-200 hover:border-brand-300 hover:bg-white/10 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
                >
                  {t('home.hero.secondaryCta')}
                  <ArrowUpRight
                    className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={2.75}
                  />
                </LocalizedLink>
              </div>
            </div>

            {/* Right-side metrics column */}
            <div className="lg:col-span-4">
              <div className="relative border-l-[3px] border-brand-500 pl-6 lg:pl-8">
                {/* Coordinate marks */}
                <div
                  aria-hidden="true"
                  className="absolute -left-[3px] top-0 flex flex-col"
                >
                  <span className="block h-4 w-[3px] bg-white" />
                </div>
                <div
                  aria-hidden="true"
                  className="absolute -left-[3px] bottom-0 flex flex-col"
                >
                  <span className="block h-4 w-[3px] bg-white" />
                </div>

                <dl className="space-y-6">
                  {[
                    ['home.hero.metric1Value', 'home.hero.metric1Label'],
                    ['home.hero.metric2Value', 'home.hero.metric2Label'],
                    ['home.hero.metric3Value', 'home.hero.metric3Label'],
                  ].map(([valueKey, labelKey], i) => (
                    <div
                      key={valueKey}
                      className={cn(
                        'flex items-baseline gap-4',
                        i !== 0 && 'pt-6 border-t border-white/10'
                      )}
                    >
                      <dt className="shrink-0 font-display text-3xl md:text-4xl font-bold leading-none tabular-nums text-white">
                        {t(valueKey)}
                      </dt>
                      <dd className="text-xs md:text-sm uppercase tracking-[0.18em] text-white/70 font-semibold">
                        {t(labelKey)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom brand rule + scroll hint */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-4 sm:px-6 lg:px-8 py-5 border-t border-white/10">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="block h-[3px] w-12 bg-brand-500"
            />
            <span className="text-[10px] uppercase tracking-[0.35em] text-white/55 font-semibold">
              {t('home.hero.scroll')}
            </span>
          </div>
          <ChevronDown
            aria-hidden="true"
            className="h-4 w-4 animate-bounce text-brand-300"
            strokeWidth={2.5}
          />
        </div>
      </section>

      {/* ============================================================
          2 · FEATURED MACHINES — "Macchine in pronta consegna"
          ============================================================ */}
      <Section
        tone="muted"
        spacing="lg"
        eyebrow={t('home.machinesEyebrow')}
        title={t('home.machinesTitle')}
        lead={t('home.machinesBody')}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {FEATURED_MACHINES.map((m) => (
            <MachineCard
              key={m.slug}
              slug={m.slug}
              image={m.image}
              name={m.nameKey}
              description={t(m.descKey)}
              category={t(m.categoryKey)}
            />
          ))}
        </div>

        <div className="mt-12 flex justify-start">
          <button
            type="button"
            onClick={() => navigate('machines')}
            className="group inline-flex items-center gap-3 rounded-sm border-2 border-brand-600 bg-white px-6 py-3 text-sm font-bold uppercase tracking-[0.1em] text-brand-700 transition-[background-color,color,transform] duration-200 hover:bg-brand-600 hover:text-white hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            {t('home.machinesCta')}
            <ArrowRight
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              strokeWidth={2.75}
            />
          </button>
        </div>
      </Section>

      {/* ============================================================
          3 · SERVICES TEASERS — grid of 6
          ============================================================ */}
      <section
        aria-labelledby="home-services-title"
        className="relative isolate overflow-hidden bg-ink-900 text-white py-20 md:py-24"
      >
        {/* Diagonal backdrop */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-2/5 opacity-[0.07] fx-diagonal-accent"
        />
        {/* Brand glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-48 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-brand-500/15 blur-3xl"
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <header className="mb-12 md:mb-16 max-w-3xl">
            <p className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-300">
              <span aria-hidden="true" className="block h-px w-10 bg-brand-500" />
              {t('home.servicesEyebrow')}
            </p>
            <h2
              id="home-services-title"
              className="fx-text-balance font-bold leading-[1.1] text-3xl sm:text-4xl md:text-[2.5rem] text-white"
            >
              {t('home.servicesTitle')}
            </h2>
            <p className="mt-4 max-w-2xl fx-text-pretty text-base md:text-lg leading-relaxed text-white/75">
              {t('home.servicesBody')}
            </p>
          </header>

          {/* Grid */}
          <ul
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10"
            role="list"
          >
            {FEATURED_SERVICES.map((s) => {
              const Icon = s.icon
              const sectionId = t(`services.sectionIds.${s.key}`)
              const href = `/${currentLang}/${servicesSegment}#${sectionId}`
              return (
                <li
                  key={s.key}
                  className="group relative bg-ink-900"
                >
                  <LocalizedLink
                    to={`${servicesSegment}#${sectionId}`}
                    className="relative flex h-full flex-col gap-4 p-7 md:p-8 transition-colors duration-300 hover:bg-brand-900/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-inset"
                    aria-label={`${t(`services.items.${s.key}.title`)} — ${t('buttons.learnMore')}`}
                  >
                    {/* Absolute-position href for link pattern */}
                    <span aria-hidden className="sr-only">
                      {href}
                    </span>

                    {/* Top coordinate tick row */}
                    <div
                      aria-hidden="true"
                      className="absolute top-0 left-0 flex gap-1 p-3"
                    >
                      <span className="block h-[3px] w-5 bg-brand-500" />
                      <span className="block h-[3px] w-2 bg-brand-500/60" />
                    </div>
                    {/* Index number */}
                    <span
                      aria-hidden="true"
                      className="absolute top-3 right-4 text-[10px] font-bold uppercase tracking-[0.25em] text-white/30 tabular-nums"
                    >
                      {String(FEATURED_SERVICES.indexOf(s) + 1).padStart(2, '0')}
                    </span>

                    <div className="mt-4 flex h-14 w-14 items-center justify-center rounded-sm border border-brand-500/40 bg-brand-500/10 text-brand-300 transition-colors duration-300 group-hover:border-brand-400 group-hover:bg-brand-500 group-hover:text-white">
                      <Icon className="h-6 w-6" strokeWidth={2} aria-hidden="true" />
                    </div>

                    <h3 className="font-bold text-lg md:text-xl leading-snug text-white">
                      {t(`services.items.${s.key}.title`)}
                    </h3>

                    <p className="text-sm md:text-[15px] leading-relaxed text-white/70 fx-text-pretty">
                      {t(`home.servicesTeasers.${s.key}`)}
                    </p>

                    <span className="mt-auto inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-brand-300 group-hover:text-brand-200 transition-colors">
                      {t('buttons.learnMore')}
                      <ArrowUpRight
                        className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        strokeWidth={2.75}
                      />
                    </span>
                  </LocalizedLink>
                </li>
              )
            })}
          </ul>

          <div className="mt-12 flex">
            <button
              type="button"
              onClick={() => navigate('services')}
              className="group inline-flex items-center gap-3 rounded-sm border-2 border-brand-400 bg-transparent px-6 py-3 text-sm font-bold uppercase tracking-[0.1em] text-brand-200 transition-[background-color,color,transform] duration-200 hover:bg-brand-500 hover:text-white hover:border-brand-500 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
            >
              {t('home.servicesCta')}
              <ArrowRight
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                strokeWidth={2.75}
              />
            </button>
          </div>
        </div>
      </section>

      {/* ============================================================
          4 · NEWS & EVENTS PREVIEW — mixed grid
          ============================================================ */}
      <Section
        tone="muted"
        spacing="lg"
        eyebrow={t('home.newsEyebrow')}
        title={t('home.newsTitle')}
        lead={t('home.newsBody')}
      >
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-x-12 lg:grid-rows-[auto_1fr_1fr]">
          {/* Left column — News */}
          <div className="flex flex-col gap-6 lg:grid lg:row-span-3 lg:grid-rows-subgrid lg:gap-6">
            <div className="flex items-center justify-between gap-4">
              <h3 className="inline-flex items-center gap-3 text-sm font-bold uppercase tracking-[0.18em] text-ink-700">
                <span aria-hidden="true" className="block h-[3px] w-8 bg-brand-500" />
                {t('home.newsLatestTitle')}
              </h3>
              <LocalizedLink
                to="news"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-brand-600 hover:text-brand-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
              >
                {t('home.newsCta')}
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.75} />
              </LocalizedLink>
            </div>

            {FEATURED_NEWS.map((n) => (
              <NewsCard
                key={n.slug}
                slug={n.slug}
                image={n.image}
                date={n.date}
                title={t(n.titleKey)}
                excerpt={t(n.excerptKey)}
              />
            ))}

            <div className="sm:hidden">
              <button
                type="button"
                onClick={() => navigate(newsSegment)}
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-brand-600 hover:text-brand-700 transition-colors"
              >
                {t('home.newsCta')}
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.75} />
              </button>
            </div>
          </div>

          {/* Right column — Events */}
          <div className="flex flex-col gap-6 lg:grid lg:row-span-3 lg:grid-rows-subgrid lg:gap-6">
            <div className="flex items-center justify-between gap-4">
              <h3 className="inline-flex items-center gap-3 text-sm font-bold uppercase tracking-[0.18em] text-ink-700">
                <span aria-hidden="true" className="block h-[3px] w-8 bg-brand-500" />
                {t('home.newsEventsTitle')}
              </h3>
              <LocalizedLink
                to="events"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-brand-600 hover:text-brand-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
              >
                {t('home.eventsCta')}
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.75} />
              </LocalizedLink>
            </div>

            {FEATURED_EVENTS.map((e) => (
              <EventCard
                key={e.slug}
                slug={e.slug}
                image={e.image}
                date={e.date}
                title={t(e.titleKey)}
                location={t(e.locationKey)}
              />
            ))}

            <div className="sm:hidden">
              <button
                type="button"
                onClick={() => navigate(eventsSegment)}
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-brand-600 hover:text-brand-700 transition-colors"
              >
                {t('home.eventsCta')}
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.75} />
              </button>
            </div>
          </div>
        </div>
      </Section>
    </main>
  )
}
